'use client';

import { Fragment, useLayoutEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { MapPin } from 'lucide-react';
import { LandmarkProjectType } from '@/types/landmark-project';
import { resolveStrapiMediaUrl } from '@/lib/strapi-media-url';
import Container from '@/components/ui/Container/Container';
import CmsImage from '@/components/ui/CmsImage';
import { MEDIA_PLACEHOLDER } from '@/lib/media-placeholder';

gsap.registerPlugin(ScrollTrigger);

const MILESTONE_BG = '#f8f7f4';

function projectImageUrl(project: LandmarkProjectType): string | null {
  const url = project.featured_image?.url;
  if (!url) return null;
  return resolveStrapiMediaUrl(url) ?? url;
}

/** Build the scrubbed reveal timeline for a single milestone article row. */
function buildMilestoneRowTimeline(article: HTMLElement, index: number) {
  const loc = article.querySelector<HTMLElement>('[data-milestone-loc]');
  const title = article.querySelector<HTMLElement>('[data-milestone-title]');
  const body = article.querySelector<HTMLElement>('[data-milestone-body]');
  const imgShell = article.nextElementSibling?.querySelector<HTMLElement>('[data-milestone-img]');

  const clipHidden = 'polygon(0% 100%, 100% 100%, 100% 100%, 0% 100%)';
  const clipVisible = 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)';

  const rowTl = gsap.timeline({
    defaults: { ease: 'none' },
    scrollTrigger: {
      trigger: article,
      start: index === 0 ? 'top 88%' : 'top 82%',
      end: 'top 22%',
      scrub: 0.75,
      invalidateOnRefresh: true,
    },
  });

  if (loc) {
    rowTl.fromTo(loc, { autoAlpha: 0, y: 22 }, { autoAlpha: 1, y: 0, duration: 0.35 }, 0);
  }
  if (title) {
    rowTl.fromTo(title, { autoAlpha: 0, y: 28 }, { autoAlpha: 1, y: 0, duration: 0.36 }, '<0.14');
  }
  if (body) {
    rowTl.fromTo(body, { autoAlpha: 0, y: 24 }, { autoAlpha: 1, y: 0, duration: 0.4 }, '<0.14');
  }
  if (imgShell) {
    rowTl.fromTo(
      imgShell,
      { clipPath: clipHidden },
      { clipPath: clipVisible, duration: 0.5 },
      0.08,
    );
  }
}

interface LandmarkProjectsListProps {
  projects: LandmarkProjectType[];
  locale: string;
  heading?: string;
  subheadingHtml?: string | null;
}

function MilestoneCopy({
  project,
  className = '',
}: {
  project: LandmarkProjectType;
  className?: string;
}) {
  const hasProjectDetails = Boolean(
    (project.short_description && project.short_description.trim()) ||
    (project.description && project.description.trim()),
  );

  return (
    <div className={`project_name ${className}`}>
      <p
        data-milestone-loc
        className="motion-safe:opacity-0 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-black/55 sm:text-[11px] rtl:flex-row-reverse"
      >
        <MapPin className="h-3 w-3 shrink-0 text-[#ff3b3b]" aria-hidden />
        {project.location}
      </p>

      <h3
        data-milestone-title
        className="motion-safe:opacity-0 mt-4 text-[1.5rem] font-bold leading-[1.2] tracking-tight text-gray-900 sm:text-[1.75rem] lg:mt-5 lg:text-[1.85rem] xl:text-[2rem]"
      >
        {project.title}
      </h3>

      {hasProjectDetails && (
        <div
          data-milestone-body
          className="motion-safe:opacity-0 mt-6 space-y-5 text-[0.9375rem] leading-[1.65] text-black/70 md:text-base"
        >
          {project.short_description?.trim() && (
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-black/80">
                THE IMPACT
              </p>
              <p className="mt-2.5">{project.short_description}</p>
            </div>
          )}
          {project.description?.trim() && (
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-black/80">
                THE STORY
              </p>
              <p className="mt-2.5 whitespace-pre-line">{project.description}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function LandmarkProjectsList({
  projects,
  heading,
  subheadingHtml,
}: LandmarkProjectsListProps) {
  const sectionRef = useRef<HTMLElement>(null);
  const hasHeading = Boolean(heading?.trim());
  const hasSubheading = Boolean(subheadingHtml?.trim());

  useLayoutEffect(() => {
    const section = sectionRef.current;
    if (!section || projects.length === 0) return;

    const reducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const ctx = gsap.context(() => {
      const intro = section.querySelector('[data-milestones-intro]');
      if (intro) {
        gsap.fromTo(
          intro.children,
          { autoAlpha: 0, y: 36 },
          {
            autoAlpha: 1,
            y: 0,
            duration: 0.95,
            stagger: 0.12,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: intro,
              start: 'top 88%',
              toggleActions: 'play none none reverse',
            },
          },
        );
      }

      if (reducedMotion) {
        section
          .querySelectorAll('[data-milestone-loc],[data-milestone-title],[data-milestone-body]')
          .forEach((el) => {
            gsap.set(el, { clearProps: 'opacity,visibility,y' });
          });
        section.querySelectorAll('[data-milestone-img]').forEach((el) => {
          gsap.set(el, { clearProps: 'clipPath' });
        });
        return;
      }

      const articles = section.querySelectorAll<HTMLElement>('[data-milestone-article]');

      articles.forEach((article, index) => {
        buildMilestoneRowTimeline(article, index);
      });
    }, section);

    const onResize = () => ScrollTrigger.refresh();
    window.addEventListener('resize', onResize);

    requestAnimationFrame(() => ScrollTrigger.refresh());

    return () => {
      window.removeEventListener('resize', onResize);
      ctx.revert();
    };
  }, [projects]);

  if (!hasHeading && !hasSubheading && projects.length === 0) {
    return null;
  }

  return (
    <section
      ref={sectionRef}
      data-section-blur="false"
      className="bg-[#f8f7f4] py-10 text-black sm:py-12 md:py-14"
      style={{ backgroundColor: MILESTONE_BG }}
      aria-label={hasHeading ? undefined : 'National milestones'}
      aria-labelledby={hasHeading ? 'national-milestones-heading' : undefined}
    >
      <Container className="mb-8 md:mb-10" size="lg">
        {(hasHeading || hasSubheading) && (
          <div data-milestones-intro>
            {hasHeading ? (
              <h2
                id="national-milestones-heading"
                className="text-center text-3xl font-bold tracking-tight text-gray-900 md:text-4xl lg:text-[2.75rem] lg:leading-tight"
              >
                {heading}
              </h2>
            ) : null}
            {hasSubheading ? (
              <div
                className={`mx-auto max-w-2xl text-center text-base leading-relaxed text-black/60 md:text-lg [&_p]:m-0 ${
                  hasHeading ? 'mt-4' : ''
                }`}
                dangerouslySetInnerHTML={{ __html: subheadingHtml! }}
              />
            ) : null}
          </div>
        )}
      </Container>

      <Container size="full" className="!max-w-[min(100%,120rem)] px-4 sm:px-6 lg:px-10">
        <div
          className="project_cms_wrap project_grid w-dyn-list grid grid-cols-1 items-start gap-y-10 lg:grid-cols-[minmax(0,340px)_minmax(0,1fr)] lg:gap-x-16 lg:gap-y-0 xl:grid-cols-[minmax(0,380px)_minmax(0,1fr)] xl:gap-x-20"
          style={{ backgroundColor: MILESTONE_BG }}
          role="list"
        >
          {projects.map((project, index) => {
            const imgSrc = projectImageUrl(project);
            return (
              <Fragment key={project.id}>
                <article
                  role="listitem"
                  data-milestone-article
                  className="project_item w-dyn-item project_item_wrap overflow-hidden lg:max-w-[min(100%,380px)] lg:justify-self-start lg:pt-1"
                  style={{ backgroundColor: MILESTONE_BG }}
                >
                  <MilestoneCopy project={project} />
                </article>
                <div
                  className="project_img_wrap relative w-full lg:min-w-0 lg:justify-self-stretch"
                  style={{ backgroundColor: MILESTONE_BG }}
                >
                  <div
                    data-milestone-img
                    className="project_img relative aspect-[16/10] w-full overflow-hidden rounded-md bg-neutral-200 ring-1 ring-black/10 will-change-[clip-path] motion-safe:[clip-path:polygon(0%_100%,100%_100%,100%_100%,0%_100%)] md:rounded-lg"
                  >
                    {imgSrc ? (
                      <CmsImage
                        src={imgSrc}
                        alt={project.featured_image?.alternativeText || project.title}
                        fill
                        className="object-cover"
                        sizes="(max-width: 1024px) 100vw, 62vw"
                        quality={90}
                        priority={index === 0}
                        fetchPriority={index === 0 ? 'high' : 'auto'}
                        fallbackSrc={MEDIA_PLACEHOLDER}
                      />
                    ) : (
                      <CmsImage
                        src={MEDIA_PLACEHOLDER}
                        alt={project.title}
                        fill
                        className="object-cover"
                        sizes="(max-width: 1024px) 100vw, 62vw"
                        fallbackSrc={MEDIA_PLACEHOLDER}
                      />
                    )}
                  </div>
                </div>
              </Fragment>
            );
          })}
        </div>
      </Container>

      {projects.length === 0 && (
        <Container>
          <p className="py-16 text-center text-gray-500">
            No milestone projects available at the moment.
          </p>
        </Container>
      )}
    </section>
  );
}
